/**
 * CSS scanning helpers shared by the band swap: comment- and string-aware
 * brace/paren/bracket matching, selector-list and compound splitting, and the
 * tokens a compound names. Pure text functions; no DOM.
 */

/** If `i` opens a comment or string, return the index just past it; else -1. */
export function skipCommentOrString(text: string, i: number): number {
  if (text[i] === '/' && text[i + 1] === '*') {
    const end = text.indexOf('*/', i + 2);
    return end < 0 ? text.length : end + 2;
  }
  if (text[i] === '"' || text[i] === "'") {
    const q = text[i];
    let j = i + 1;
    while (j < text.length) {
      if (text[j] === '\\') { j += 2; continue; }
      if (text[j] === q) { j += 1; break; }
      j += 1;
    }
    return j;
  }
  return -1;
}

/** Split `text` at top-level `delim`, respecting (), [], quotes and comments. Delimiter is dropped. */
export function splitTopLevel(text: string, delim: string): string[] {
  const out: string[] = [];
  let start = 0;
  let i = 0;
  let paren = 0;
  let bracket = 0;
  while (i < text.length) {
    const skip = skipCommentOrString(text, i);
    if (skip >= 0) { i = skip; continue; }
    const c = text[i];
    if (c === '(') paren += 1;
    else if (c === ')') paren = Math.max(0, paren - 1);
    else if (c === '[') bracket += 1;
    else if (c === ']') bracket = Math.max(0, bracket - 1);
    else if (c === delim && paren === 0 && bracket === 0) {
      out.push(text.slice(start, i));
      start = i + 1;
    }
    i += 1;
  }
  out.push(text.slice(start));
  return out;
}

/** Index of the `}` matching the `{` at `openIdx`, comment/string aware. */
export function matchBrace(text: string, openIdx: number): number {
  let depth = 0;
  let i = openIdx;
  while (i < text.length) {
    const skip = skipCommentOrString(text, i);
    if (skip >= 0) { i = skip; continue; }
    const c = text[i];
    if (c === '{') depth += 1;
    else if (c === '}') { depth -= 1; if (depth === 0) return i; }
    i += 1;
  }
  return text.length;
}

/** Index of the matching `)` for the `(` at `openIdx`, comment/string aware. */
export function matchParen(text: string, openIdx: number): number {
  let depth = 0;
  let i = openIdx;
  while (i < text.length) {
    const skip = skipCommentOrString(text, i);
    if (skip >= 0) { i = skip; continue; }
    const c = text[i];
    if (c === '(') depth += 1;
    else if (c === ')') { depth -= 1; if (depth === 0) return i; }
    i += 1;
  }
  return text.length;
}

/** Index of the next top-level `{` from `from`, ignoring () [] contents, comments and strings; -1 if none. */
export function nextTopBrace(text: string, from: number): number {
  let i = from;
  let paren = 0;
  let bracket = 0;
  while (i < text.length) {
    const skip = skipCommentOrString(text, i);
    if (skip >= 0) { i = skip; continue; }
    const c = text[i];
    if (c === '(') paren += 1;
    else if (c === ')') paren = Math.max(0, paren - 1);
    else if (c === '[') bracket += 1;
    else if (c === ']') bracket = Math.max(0, bracket - 1);
    else if (c === '{' && paren === 0 && bracket === 0) return i;
    i += 1;
  }
  return -1;
}

export const firstAtName = (prelude: string): string => /^@[\w-]+/.exec(prelude.trim())?.[0] ?? prelude.trim();

/**
 * A prelude span can carry `;`-terminated at-statements (@charset, @import, ...)
 * ahead of the selector that owns the following `{...}`. Split at the last
 * top-level `;` so those statements are handled on their own and only the real
 * selector is classified.
 */
export function splitLeadingStatements(prelude: string): { statements: string; selector: string } {
  let lastSemi = -1;
  let i = 0;
  let paren = 0;
  let bracket = 0;
  while (i < prelude.length) {
    const skip = skipCommentOrString(prelude, i);
    if (skip >= 0) { i = skip; continue; }
    const c = prelude[i];
    if (c === '(') paren += 1;
    else if (c === ')') paren = Math.max(0, paren - 1);
    else if (c === '[') bracket += 1;
    else if (c === ']') bracket = Math.max(0, bracket - 1);
    else if (c === ';' && paren === 0 && bracket === 0) lastSemi = i;
    i += 1;
  }
  if (lastSemi < 0) return { statements: '', selector: prelude };
  return { statements: prelude.slice(0, lastSemi + 1), selector: prelude.slice(lastSemi + 1) };
}

/** The selector that owns a `{...}`, with any leading `;`-statements stripped and trimmed. */
export function effectiveSelector(prelude: string): string {
  return splitLeadingStatements(prelude).selector.trim();
}

/**
 * Top-level `{...}` blocks of a stylesheet, comment/string-aware so a `content:"}"`
 * or `content:"{"` declaration never ends or opens a block and `@media` bodies stay
 * whole. Both the opening brace and its match are found with the shared scanners.
 */
export function cssBlocks(css: string): Array<{ prelude: string; body: string }> {
  const out: Array<{ prelude: string; body: string }> = [];
  let i = 0;
  while (i < css.length) {
    const open = nextTopBrace(css, i);
    if (open < 0) break;
    const close = matchBrace(css, open);
    out.push({ prelude: css.slice(i, open).trim(), body: css.slice(open + 1, close) });
    i = close + 1;
  }
  return out;
}

/** Strip `/*…*\/` comments, but never comment-looking text inside a string (`content:"/*x*\/"` stays). */
export function stripComments(css: string): string {
  let out = '';
  let i = 0;
  let run = 0; // start of the current plain run, copied in spans
  while (i < css.length) {
    const c = css[i];
    if (c === '/' && css[i + 1] === '*') {
      out += css.slice(run, i);
      const end = css.indexOf('*/', i + 2);
      i = end < 0 ? css.length : end + 2;
      run = i;
      continue;
    }
    if (c === '"' || c === "'") {
      i = skipCommentOrString(css, i); // keep the whole string in the pending run
      continue;
    }
    i += 1;
  }
  return out + css.slice(run, i);
}

export function varsRead(text: string): string[] {
  return [...text.matchAll(/var\(\s*(--[\w-]+)/g)].map((m) => m[1]);
}

export type Compound = { text: string; combinator: string };

/** Split one selector arm into compounds at top-level combinators; `combinator` precedes the compound. */
export function compounds(arm: string): Compound[] {
  const out: Compound[] = [];
  let i = 0;
  const n = arm.length;
  while (i < n) {
    let sawSpace = false;
    let explicit = '';
    while (i < n) {
      const c = arm[i];
      if (/\s/.test(c)) { sawSpace = true; i += 1; }
      else if (c === '>' || c === '+' || c === '~') { explicit = c; i += 1; }
      else break;
    }
    if (i >= n) break;
    let combinator = '';
    if (out.length === 0) combinator = '';
    else if (explicit) combinator = explicit;
    else if (sawSpace) combinator = ' ';
    const start = i;
    let paren = 0;
    let bracket = 0;
    while (i < n) {
      const c = arm[i];
      const skip = skipCommentOrString(arm, i);
      if (skip >= 0) { i = skip; continue; }
      if (c === '(') { paren += 1; i += 1; continue; }
      if (c === ')') { paren = Math.max(0, paren - 1); i += 1; continue; }
      if (c === '[') { bracket += 1; i += 1; continue; }
      if (c === ']') { bracket = Math.max(0, bracket - 1); i += 1; continue; }
      if (paren === 0 && bracket === 0 && (/\s/.test(c) || c === '>' || c === '+' || c === '~')) break;
      i += 1;
    }
    out.push({ text: arm.slice(start, i), combinator });
  }
  return out;
}

/** Top-level tokens of one compound: leading type/`*`, and ids/classes outside () and []. */
export function compoundTokens(compound: string): { type: string | null; ids: string[]; classes: string[] } {
  const type = /^(\*|-?[_a-zA-Z][\w-]*)/.exec(compound)?.[1] ?? null;
  const ids: string[] = [];
  const classes: string[] = [];
  let paren = 0;
  let bracket = 0;
  for (let i = 0; i < compound.length; i += 1) {
    const skip = skipCommentOrString(compound, i);
    if (skip >= 0) { i = skip - 1; continue; }
    const c = compound[i];
    if (c === '(') { paren += 1; continue; }
    if (c === ')') { paren = Math.max(0, paren - 1); continue; }
    if (c === '[') { bracket += 1; continue; }
    if (c === ']') { bracket = Math.max(0, bracket - 1); continue; }
    if (paren || bracket) continue;
    if (c === '#') {
      const m = /^#(-?[_a-zA-Z][\w-]*)/.exec(compound.slice(i));
      if (m) { ids.push(m[1]); i += m[0].length - 1; }
    } else if (c === '.') {
      const m = /^\.(-?[_a-zA-Z][\w-]*)/.exec(compound.slice(i));
      if (m) { classes.push(m[1]); i += m[0].length - 1; }
    }
  }
  return { type, ids, classes };
}

/** Every id/class an arm names anywhere (including inside functional pseudos), ignoring [attr] values. */
export function namedTokens(arm: string): { ids: string[]; classes: string[] } {
  const bare = arm.replace(/\[[^\]]*\]/g, ' ');
  return {
    ids: [...bare.matchAll(/#(-?[_a-zA-Z][\w-]*)/g)].map((m) => m[1]),
    classes: [...bare.matchAll(/\.(-?[_a-zA-Z][\w-]*)/g)].map((m) => m[1]),
  };
}

/** `#id` tokens anywhere in a text fragment, ignoring [attr] values. */
export function idTokensOf(text: string): string[] {
  return [...text.replace(/\[[^\]]*\]/g, ' ').matchAll(/#(-?[_a-zA-Z][\w-]*)/g)].map((m) => m[1]);
}

/** Insert `marker` into a compound: after its leading type/`*` if present, else at its start. */
export function insertMarker(compound: string, marker: string): string {
  const m = /^(\*|-?[_a-zA-Z][\w-]*)/.exec(compound);
  if (m) return m[1] + marker + compound.slice(m[1].length);
  return marker + compound;
}

export function joinCompounds(comps: Compound[]): string {
  return comps
    .map((c, i) => {
      if (i === 0) return c.text;
      if (c.combinator === ' ') return ` ${c.text}`;
      return ` ${c.combinator} ${c.text}`;
    })
    .join('');
}

/** Drop leading whitespace and `/*…*\/` comments so an at-rule's first line can be classified. */
export function stripLeadingComments(s: string): string {
  let i = 0;
  const n = s.length;
  for (;;) {
    while (i < n && /\s/.test(s[i])) i += 1;
    if (s[i] === '/' && s[i + 1] === '*') {
      const end = s.indexOf('*/', i + 2);
      if (end < 0) { i = n; break; }
      i = end + 2;
      continue;
    }
    break;
  }
  return s.slice(i);
}

/** Index of the `]` matching the `[` at `openIdx`, comment/string aware. */
export function matchBracket(text: string, openIdx: number): number {
  let depth = 0;
  let i = openIdx;
  while (i < text.length) {
    const skip = skipCommentOrString(text, i);
    if (skip >= 0) { i = skip; continue; }
    const c = text[i];
    if (c === '[') depth += 1;
    else if (c === ']') { depth -= 1; if (depth === 0) return i; }
    i += 1;
  }
  return text.length;
}

/** The pseudo-class name that a top-level `(` belongs to (e.g. `:has`, `:not`, `:-webkit-any`), or ''. */
export function pseudoBefore(text: string, parenIdx: number): string {
  return /(:{1,2}[-\w]+)$/.exec(text.slice(0, parenIdx))?.[1]?.toLowerCase() ?? '';
}

export const TARGET_PSEUDOS = new Set([':is', ':where', ':matches', ':-webkit-any']);
